/**
 * Specification Categories Hook
 * 
 * Manages specification categories used for organizing specification field definitions.
 * Categories provide hierarchical structure for grouping related specification fields,
 * making it easier to build and navigate complex product specifications.
 * 
 * **Category Hierarchy:**
 * - Top-level: Product Categories (e.g., "Electronics")
 * - Mid-level: Specification Categories (e.g., "Power Requirements", "Physical Dimensions")
 * - Bottom-level: Specification Fields (e.g., "Voltage", "Length", "Width")
 * 
 * **Key Features:**
 * - Fetch categories (active only or including inactive)
 * - Link categories to product categories (optional)
 * - Create, update, delete specification categories
 * - Display order management for UI presentation
 * - Soft delete (deactivate) for data integrity
 * - Icon and description support
 * 
 * **Category Lifecycle:**
 * 1. **Active**: Visible in specification builders
 * 2. **Inactive**: Hidden but preserved for existing specs
 * 3. **Deleted**: Permanently removed (cascades to fields)
 * 
 * **Product Category Association:**
 * - Categories can be linked to product categories
 * - Enables category-specific specification templates
 * - Optional relationship (can be null)
 * 
 * **Use Cases:**
 * - Specification builder category organization
 * - Template creation with category grouping
 * - Admin category management
 * - RFQ specification structuring
 * - Vendor capability definitions
 * 
 * @param {boolean} includeInactive - Whether to fetch inactive categories
 * 
 * @returns {Object} Specification categories state
 * @returns {SpecificationCategory[]} categories - Array of categories
 * @returns {boolean} loading - Loading state
 * @returns {string | null} error - Error message if operation failed
 * @returns {Function} createCategory - Create new category
 * @returns {Function} updateCategory - Update existing category
 * @returns {Function} deleteCategory - Permanently delete category
 * @returns {Function} deactivateCategory - Soft delete category
 * @returns {Function} refetch - Manually refresh categories
 * 
 * @example
 * // Fetch active categories for specification builder
 * const { categories, loading } = useSpecificationCategories();
 * 
 * categories.map(category => (
 *   <CategorySection key={category.id} title={category.name}>
 *     <SpecificationFields categoryId={category.id} />
 *   </CategorySection>
 * ));
 * 
 * @example
 * // Admin management with inactive categories
 * const {
 *   categories,
 *   createCategory,
 *   updateCategory,
 *   deactivateCategory
 * } = useSpecificationCategories(true);
 * 
 * // Create category linked to product category
 * await createCategory({
 *   name: 'Electrical Specifications',
 *   description: 'Power and electrical requirements',
 *   display_order: 1,
 *   product_category_id: productCategoryId
 * });
 * 
 * @example
 * // Update category order
 * await updateCategory(categoryId, {
 *   display_order: 5
 * });
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface SpecificationCategory {
  id: string;
  public_id: string;
  name: string;
  description: string | null;
  icon: string | null;
  display_order: number;
  is_active: boolean;
  product_category_id: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

interface CreateCategoryData {
  name: string;
  description?: string;
  icon?: string;
  display_order: number;
  product_category_id?: string;
}

interface UpdateCategoryData {
  name?: string;
  description?: string;
  icon?: string;
  display_order?: number;
  is_active?: boolean;
  product_category_id?: string;
}

export const useSpecificationCategories = (includeInactive: boolean = false) => {
  const [categories, setCategories] = useState<SpecificationCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('specification_categories')
        .select('*')
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setCategories(data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch categories';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [includeInactive]);

  const createCategory = async (categoryData: CreateCategoryData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: createError } = await supabase
        .from('specification_categories')
        .insert({
          ...categoryData,
          created_by_user_id: user.id,
        })
        .select()
        .single();

      if (createError) throw createError;

      toast({
        title: 'Success',
        description: 'Category created successfully',
      });

      await fetchCategories();
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create category';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  const updateCategory = async (id: string, updates: UpdateCategoryData) => {
    try {
      const { error: updateError } = await supabase
        .from('specification_categories')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Category updated successfully',
      });

      await fetchCategories();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update category';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('specification_categories')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Success',
        description: 'Category deleted successfully',
      });

      await fetchCategories();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete category';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  const deactivateCategory = async (id: string) => {
    await updateCategory(id, { is_active: false });
  };

  return {
    categories,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
    deactivateCategory,
    refetch: fetchCategories,
  };
};
